import type {
  AccountBillingResponse,
  AccountAgentUsageResponse,
  AccountMeResponse,
  AccountOnboardingStateResponse,
  AddMonitorItemRequest,
  AddCommitteeToMonitorListRequest,
  AddFecCommitteeToMonitorListRequest,
  AddMemberToMonitorListRequest,
  AddMonitorListBillRequest,
  AgentModelTier,
  AgentMode,
  AgentPageContext,
  AgentRunResponse,
  AgentThreadDetailResponse,
  AgentThreadsResponse,
  BillingSessionResponse,
  CreateOnboardingSessionRequest,
  CreateBillingCheckoutSessionRequest,
  CreateBillingPortalSessionRequest,
  CreateCommitteeMonitorListRequest,
  CreateFecCommitteeMonitorListRequest,
  CreateMonitorRequest,
  DeleteCommitteeMonitorListResponse,
  DeleteFecCommitteeMonitorListResponse,
  DeleteMonitorBuilderDraftResponse,
  DeleteUnifiedMonitorResponse,
  CreateMemberMonitorListRequest,
  DeleteMemberMonitorListResponse,
  CreateMonitorListRequest,
  FecCommitteeDetailResponse,
  MonitorBuilderDraftResponse,
  MonitorItemTarget,
  MonitorResponse,
  MonitorsResponse,
  MonitorCommitteeListDetailResponse,
  MonitorCommitteeListResponse,
  MonitorCommitteeListsResponse,
  MonitorFecCommitteeListResponse,
  MonitorFecCommitteeListsResponse,
  MonitorOverviewResponse,
  TrackedActivityResponse,
  DeleteMonitorListResponse,
  MonitorMemberListDetailResponse,
  MonitorMemberListResponse,
  MonitorMemberListsResponse,
  MonitorListDetailResponse,
  MonitorListResponse,
  MonitorListsResponse,
  RemoveCommitteesFromMonitorListRequest,
  RemoveMembersFromMonitorListRequest,
  RemoveMonitorListBillsRequest,
  RemoveMonitorItemsRequest,
  NotificationPreferencesResponse,
  SelectOnboardingAccessRequest,
  SelectOnboardingAccessResponse,
  SaveMonitorBuilderProposalRequest,
  SaveMonitorBuilderProposalResponse,
  UpdateCommitteeMonitorListRequest,
  UpdateFecCommitteeMonitorListRequest,
  UpdateMemberMonitorListRequest,
  UpdateMonitorItemPriorityRequest,
  UpdateMonitorRequest,
  UpdateMonitorListRequest,
  UpdateNotificationPreferencesRequest,
  UpdateOnboardingProfileRequest,
  MemberFinanceDetailResponse,
  MemberVoteProfileResponse,
  MemberVoteRecordFilter,
  MemberVoteRecordResponse,
  WorkspaceUpcomingResponse,
} from "../../shared/types";

type AgentThreadsOptions = {
  limit?: number;
  mode?: AgentMode;
};

type RunAgentResearchRequest = {
  clientRequestId?: string;
  message: string;
  mode?: AgentMode;
  modelTier?: AgentModelTier;
  pageContext?: AgentPageContext;
  threadId?: string;
};

function monitorItemSearchParams(item: MonitorItemTarget): URLSearchParams {
  const params = new URLSearchParams();
  params.set("itemType", item.itemType);
  if (item.itemType === "bill") {
    params.set("billId", item.billId);
  } else if (item.itemType === "member") {
    params.set("bioguideId", item.bioguideId);
  } else if (item.itemType === "committee") {
    params.set("committeeCode", item.committeeCode);
    params.set("congress", String(item.congress));
  } else {
    params.set("committeeId", item.committeeId);
  }
  return params;
}

export class ApiRequestError extends Error {
  readonly code: string | null;
  readonly details: unknown;
  readonly status: number;

  constructor(message: string, status: number, code?: string | null, details?: unknown) {
    super(message);
    this.name = "ApiRequestError";
    this.code = code ?? null;
    this.details = details;
    this.status = status;
  }
}

async function accountFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const headers = new Headers(init?.headers);
  headers.set("Accept", "application/json");

  if (init?.body && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }

  const response = await fetch(path, {
    ...init,
    cache: "no-store",
    credentials: "include",
    headers,
  });

  if (!response.ok) {
    const payload = (await response.json().catch(() => null)) as { code?: string; details?: unknown; error?: string } | null;
    throw new ApiRequestError(payload?.error ?? `Request failed: ${response.status}`, response.status, payload?.code, payload?.details);
  }

  return (await response.json()) as T;
}

export function createAccountApi() {
  return {
    me: () => accountFetch<AccountMeResponse>("/api/v1/account/me"),
    billing: () => accountFetch<AccountBillingResponse>("/api/v1/account/billing"),
    agentUsage: () => accountFetch<AccountAgentUsageResponse>("/api/v1/account/agent-usage"),
    agentThreads: (options: AgentThreadsOptions = {}) => {
      const params = new URLSearchParams();
      if (typeof options.limit === "number") {
        params.set("limit", String(options.limit));
      }
      if (options.mode) {
        params.set("mode", options.mode);
      }
      const query = params.toString();
      return accountFetch<AgentThreadsResponse>(`/api/v1/agent/threads${query ? `?${query}` : ""}`);
    },
    agentThreadDetail: (threadId: string) => accountFetch<AgentThreadDetailResponse>(`/api/v1/agent/threads/${encodeURIComponent(threadId)}`),
    agentRun: (runId: string) => accountFetch<AgentRunResponse>(`/api/v1/agent/runs/${encodeURIComponent(runId)}`),
    runAgentResearch: (payload: RunAgentResearchRequest) =>
      accountFetch<AgentRunResponse>("/api/v1/agent/runs", {
        body: JSON.stringify({ ...payload, mode: payload.mode ?? "research" }),
        method: "POST",
      }),
    onboarding: () => accountFetch<AccountOnboardingStateResponse>("/api/v1/account/onboarding"),
    notificationPreferences: () => accountFetch<NotificationPreferencesResponse>("/api/v1/account/notification-preferences"),
    createOnboardingSession: (payload: CreateOnboardingSessionRequest) =>
      accountFetch<AccountOnboardingStateResponse>("/api/v1/account/onboarding/session", {
        body: JSON.stringify(payload),
        method: "POST",
      }),
    updateOnboardingProfile: (payload: UpdateOnboardingProfileRequest) =>
      accountFetch<AccountOnboardingStateResponse>("/api/v1/account/onboarding/profile", {
        body: JSON.stringify(payload),
        method: "PATCH",
      }),
    selectOnboardingAccess: (payload: SelectOnboardingAccessRequest) =>
      accountFetch<SelectOnboardingAccessResponse>("/api/v1/account/onboarding/access", {
        body: JSON.stringify(payload),
        method: "POST",
      }),
    memberFinanceDetail: (bioguideId: string, congress?: string) =>
      accountFetch<MemberFinanceDetailResponse>(`/api/v1/members/${bioguideId}/finance${congress ? `?congress=${congress}` : ""}`),
    fecCommitteeDetail: (committeeId: string, cycle?: number) =>
      accountFetch<FecCommitteeDetailResponse>(
        `/api/v1/fec/committees/${encodeURIComponent(committeeId)}${Number.isFinite(cycle) ? `?cycle=${encodeURIComponent(String(cycle))}` : ""}`,
      ),
    memberVotes: (bioguideId: string, page = 1, filter: MemberVoteRecordFilter = "all") =>
      accountFetch<MemberVoteRecordResponse>(`/api/v1/members/${bioguideId}/votes?page=${page}&filter=${encodeURIComponent(filter)}`),
    memberVoteProfile: (bioguideId: string, congress?: string) =>
      accountFetch<MemberVoteProfileResponse>(`/api/v1/members/${bioguideId}/vote-profile${congress ? `?congress=${encodeURIComponent(congress)}` : ""}`),
    monitorLists: (billId?: string) =>
      accountFetch<MonitorListsResponse>(`/api/v1/account/monitor-lists${billId ? `?billId=${encodeURIComponent(billId)}` : ""}`),
    monitorListDetail: (listId: string) =>
      accountFetch<MonitorListDetailResponse>(`/api/v1/account/monitor-lists/${encodeURIComponent(listId)}`),
    memberMonitorLists: (bioguideId?: string) =>
      accountFetch<MonitorMemberListsResponse>(
        `/api/v1/account/member-monitor-lists${bioguideId ? `?bioguideId=${encodeURIComponent(bioguideId)}` : ""}`,
      ),
    committeeMonitorLists: (committeeCode?: string, congress?: number) =>
      accountFetch<MonitorCommitteeListsResponse>(
        `/api/v1/account/committee-monitor-lists${
          committeeCode && Number.isFinite(congress)
            ? `?committeeCode=${encodeURIComponent(committeeCode)}&congress=${encodeURIComponent(String(congress))}`
            : ""
        }`,
      ),
    fecCommitteeMonitorLists: (committeeId?: string) =>
      accountFetch<MonitorFecCommitteeListsResponse>(
        `/api/v1/account/fec-committee-monitor-lists${committeeId ? `?committeeId=${encodeURIComponent(committeeId)}` : ""}`,
      ),
    monitorOverview: () => accountFetch<MonitorOverviewResponse>("/api/v1/account/monitor-overview"),
    upcoming: () => accountFetch<WorkspaceUpcomingResponse>("/api/v1/account/upcoming"),
    unifiedMonitors: (item?: MonitorItemTarget) => {
      const query = item ? monitorItemSearchParams(item).toString() : "";
      return accountFetch<MonitorsResponse>(`/api/v1/account/monitors${query ? `?${query}` : ""}`);
    },
    unifiedMonitorDetail: (monitorId: string) =>
      accountFetch<MonitorResponse>(`/api/v1/account/monitors/${encodeURIComponent(monitorId)}`),
    monitorBuilderDraft: (draftId: string) =>
      accountFetch<MonitorBuilderDraftResponse>(`/api/v1/account/monitor-builder-drafts/${encodeURIComponent(draftId)}`),
    deleteMonitorBuilderDraft: (draftId: string) =>
      accountFetch<DeleteMonitorBuilderDraftResponse>(`/api/v1/account/monitor-builder-drafts/${encodeURIComponent(draftId)}`, {
        method: "DELETE",
      }),
    createUnifiedMonitor: (payload: CreateMonitorRequest) =>
      accountFetch<MonitorResponse>("/api/v1/account/monitors", {
        body: JSON.stringify(payload),
        method: "POST",
      }),
    addUnifiedMonitorItem: (monitorId: string, payload: AddMonitorItemRequest) =>
      accountFetch<MonitorResponse>(`/api/v1/account/monitors/${encodeURIComponent(monitorId)}/items`, {
        body: JSON.stringify(payload),
        method: "POST",
      }),
    removeUnifiedMonitorItems: (monitorId: string, payload: RemoveMonitorItemsRequest) =>
      accountFetch<MonitorResponse>(`/api/v1/account/monitors/${encodeURIComponent(monitorId)}/items`, {
        body: JSON.stringify(payload),
        method: "DELETE",
      }),
    updateUnifiedMonitorItemPriority: (monitorId: string, payload: UpdateMonitorItemPriorityRequest) =>
      accountFetch<MonitorResponse>(`/api/v1/account/monitors/${encodeURIComponent(monitorId)}/items/priority`, {
        body: JSON.stringify(payload),
        method: "PATCH",
      }),
    updateUnifiedMonitor: (monitorId: string, payload: UpdateMonitorRequest) =>
      accountFetch<MonitorResponse>(`/api/v1/account/monitors/${encodeURIComponent(monitorId)}`, {
        body: JSON.stringify(payload),
        method: "PATCH",
      }),
    deleteUnifiedMonitor: (monitorId: string) =>
      accountFetch<DeleteUnifiedMonitorResponse>(`/api/v1/account/monitors/${encodeURIComponent(monitorId)}`, {
        method: "DELETE",
      }),
    saveMonitorBuilderProposal: (payload: SaveMonitorBuilderProposalRequest) =>
      accountFetch<SaveMonitorBuilderProposalResponse>("/api/v1/account/monitors/builder-confirmations", {
        body: JSON.stringify(payload),
        method: "POST",
      }),
    memberMonitorListDetail: (listId: string) =>
      accountFetch<MonitorMemberListDetailResponse>(`/api/v1/account/member-monitor-lists/${encodeURIComponent(listId)}`),
    committeeMonitorListDetail: (listId: string) =>
      accountFetch<MonitorCommitteeListDetailResponse>(`/api/v1/account/committee-monitor-lists/${encodeURIComponent(listId)}`),
    createMonitorList: (payload: CreateMonitorListRequest) =>
      accountFetch<MonitorListResponse>("/api/v1/account/monitor-lists", {
        body: JSON.stringify(payload),
        method: "POST",
      }),
    createMemberMonitorList: (payload: CreateMemberMonitorListRequest) =>
      accountFetch<MonitorMemberListResponse>("/api/v1/account/member-monitor-lists", {
        body: JSON.stringify(payload),
        method: "POST",
      }),
    createCommitteeMonitorList: (payload: CreateCommitteeMonitorListRequest) =>
      accountFetch<MonitorCommitteeListResponse>("/api/v1/account/committee-monitor-lists", {
        body: JSON.stringify(payload),
        method: "POST",
      }),
    createFecCommitteeMonitorList: (payload: CreateFecCommitteeMonitorListRequest) =>
      accountFetch<MonitorFecCommitteeListResponse>("/api/v1/account/fec-committee-monitor-lists", {
        body: JSON.stringify(payload),
        method: "POST",
      }),
    renameMonitorList: (listId: string, payload: UpdateMonitorListRequest) =>
      accountFetch<MonitorListResponse>(`/api/v1/account/monitor-lists/${encodeURIComponent(listId)}`, {
        body: JSON.stringify(payload),
        method: "PATCH",
      }),
    renameMemberMonitorList: (listId: string, payload: UpdateMemberMonitorListRequest) =>
      accountFetch<MonitorMemberListResponse>(`/api/v1/account/member-monitor-lists/${encodeURIComponent(listId)}`, {
        body: JSON.stringify(payload),
        method: "PATCH",
      }),
    renameCommitteeMonitorList: (listId: string, payload: UpdateCommitteeMonitorListRequest) =>
      accountFetch<MonitorCommitteeListResponse>(`/api/v1/account/committee-monitor-lists/${encodeURIComponent(listId)}`, {
        body: JSON.stringify(payload),
        method: "PATCH",
      }),
    renameFecCommitteeMonitorList: (listId: string, payload: UpdateFecCommitteeMonitorListRequest) =>
      accountFetch<MonitorFecCommitteeListResponse>(`/api/v1/account/fec-committee-monitor-lists/${encodeURIComponent(listId)}`, {
        body: JSON.stringify(payload),
        method: "PATCH",
      }),
    deleteMonitorList: (listId: string) =>
      accountFetch<DeleteMonitorListResponse>(`/api/v1/account/monitor-lists/${encodeURIComponent(listId)}`, {
        method: "DELETE",
      }),
    deleteMemberMonitorList: (listId: string) =>
      accountFetch<DeleteMemberMonitorListResponse>(`/api/v1/account/member-monitor-lists/${encodeURIComponent(listId)}`, {
        method: "DELETE",
      }),
    deleteCommitteeMonitorList: (listId: string) =>
      accountFetch<DeleteCommitteeMonitorListResponse>(`/api/v1/account/committee-monitor-lists/${encodeURIComponent(listId)}`, {
        method: "DELETE",
      }),
    deleteFecCommitteeMonitorList: (listId: string) =>
      accountFetch<DeleteFecCommitteeMonitorListResponse>(`/api/v1/account/fec-committee-monitor-lists/${encodeURIComponent(listId)}`, {
        method: "DELETE",
      }),
    addBillToMonitorList: (listId: string, payload: AddMonitorListBillRequest) =>
      accountFetch<MonitorListResponse>(`/api/v1/account/monitor-lists/${encodeURIComponent(listId)}/bills`, {
        body: JSON.stringify(payload),
        method: "POST",
      }),
    addMemberToMonitorList: (listId: string, payload: AddMemberToMonitorListRequest) =>
      accountFetch<MonitorMemberListResponse>(`/api/v1/account/member-monitor-lists/${encodeURIComponent(listId)}/members`, {
        body: JSON.stringify(payload),
        method: "POST",
      }),
    addCommitteeToMonitorList: (listId: string, payload: AddCommitteeToMonitorListRequest) =>
      accountFetch<MonitorCommitteeListResponse>(`/api/v1/account/committee-monitor-lists/${encodeURIComponent(listId)}/committees`, {
        body: JSON.stringify(payload),
        method: "POST",
      }),
    addFecCommitteeToMonitorList: (listId: string, payload: AddFecCommitteeToMonitorListRequest) =>
      accountFetch<MonitorFecCommitteeListResponse>(`/api/v1/account/fec-committee-monitor-lists/${encodeURIComponent(listId)}/committees`, {
        body: JSON.stringify(payload),
        method: "POST",
      }),
    removeBillsFromMonitorList: (listId: string, payload: RemoveMonitorListBillsRequest) =>
      accountFetch<MonitorListDetailResponse>(`/api/v1/account/monitor-lists/${encodeURIComponent(listId)}/bills`, {
        body: JSON.stringify(payload),
        method: "DELETE",
      }),
    removeMembersFromMonitorList: (listId: string, payload: RemoveMembersFromMonitorListRequest) =>
      accountFetch<MonitorMemberListDetailResponse>(`/api/v1/account/member-monitor-lists/${encodeURIComponent(listId)}/members`, {
        body: JSON.stringify(payload),
        method: "DELETE",
      }),
    removeCommitteesFromMonitorList: (listId: string, payload: RemoveCommitteesFromMonitorListRequest) =>
      accountFetch<MonitorCommitteeListDetailResponse>(`/api/v1/account/committee-monitor-lists/${encodeURIComponent(listId)}/committees`, {
        body: JSON.stringify(payload),
        method: "DELETE",
      }),
    updateNotificationPreferences: (payload: UpdateNotificationPreferencesRequest) =>
      accountFetch<NotificationPreferencesResponse>("/api/v1/account/notification-preferences", {
        body: JSON.stringify(payload),
        method: "PATCH",
      }),
    createBillingCheckoutSession: (payload: CreateBillingCheckoutSessionRequest = {}) =>
      accountFetch<BillingSessionResponse>("/api/v1/account/billing/checkout-session", {
        body: JSON.stringify(payload),
        method: "POST",
      }),
    createBillingPortalSession: (payload: CreateBillingPortalSessionRequest = {}) =>
      accountFetch<BillingSessionResponse>("/api/v1/account/billing/portal-session", {
        body: JSON.stringify(payload),
        method: "POST",
      }),
  };
}

export function getTrackedActivity(params: URLSearchParams): Promise<TrackedActivityResponse> {
  return accountFetch<TrackedActivityResponse>(`/api/v1/account/activity/latest?${params.toString()}`);
}
